import React from 'react';
import styles from '../style/AnalysisModal.module.css';
import InteractivePlotContainer from './InteractivePlotContainer';

// --- SVG Icons (unchanged) ---
const IconHome = () => <svg viewBox="0 0 16 16" fill="currentColor"><path d="M8.5 1.5a.5.5 0 0 0-1 0V3H6a1 1 0 0 0-1 1v2.5a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5V4.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2.5a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5V4a1 1 0 0 0-1-1H9.5V1.5Z"/><path d="M13 6.5a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 2 6.5V14a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5V6.5Z"/></svg>;
const IconBack = () => <svg viewBox="0 0 16 16" fill="currentColor"><path fillRule="evenodd" d="M12.5 15a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 1 0v13a.5.5 0 0 1-.5.5Z"/><path fillRule="evenodd" d="M12.354 8.354a.5.5 0 0 1 0-.708l-7-7a.5.5 0 0 1 .708.708L11.293 8l-5.647 5.646a.5.5 0 0 1-.708.708l7-7Z"/></svg>;
const IconForward = () => <svg viewBox="0 0 16 16" fill="currentColor"><path fillRule="evenodd" d="M3.5 15a.5.5 0 0 0 .5-.5v-13a.5.5 0 0 0-1 0v13a.5.5 0 0 0 .5.5Z"/><path fillRule="evenodd" d="M3.646 8.354a.5.5 0 0 0 0-.708l7-7a.5.5 0 0 0-.708.708L4.707 8l5.647 5.646a.5.5 0 0 0 .708.708l-7-7Z"/></svg>;
const IconPan = () => <svg viewBox="0 0 16 16" fill="currentColor"><path d="M10 4a.5.5 0 0 1 .5.5v1.21l1.646-1.647a.5.5 0 0 1 .708.708L11.21 6.5h1.29a.5.5 0 0 1 0 1h-1.29l1.647 1.646a.5.5 0 0 1-.708.708L10.5 7.79V9a.5.5 0 0 1-1 0V7.79l-1.646 1.647a.5.5 0 1 1-.708-.708L8.79 7.5H7.5a.5.5 0 0 1 0-1h1.29L7.146 4.854a.5.5 0 1 1 .708-.708L9.5 5.79V4.5a.5.5 0 0 1 .5-.5zM1.5 8a.5.5 0 0 1 .5-.5h12a.5.5 0 0 1 0 1h-12A.5.5 0 0 1 1.5 8z"/></svg>;
const IconZoom = () => <svg viewBox="0 0 16 16" fill="currentColor"><path d="M10.442 10.442a1 1 0 0 1 1.415 0l3.85 3.85a1 1 0 0 1-1.414 1.415l-3.85-3.85a1 1 0 0 1 0-1.415z"/><path d="M6.5 12a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11zM13 6.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0z"/></svg>;
const IconSave = () => <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5 0a.5.5 0 0 0-.5.5V4h-2a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V4.5a.5.5 0 0 0-.5-.5h-2V.5A.5.5 0 0 0 9 0H5zm-1 4.5V.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5V4h1v10H3V4.5h1z"/><path d="M7.5 2a.5.5 0 0 0-1 0v2h1V2z"/><path d="M5.5 10a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 0-1h-4a.5.5 0 0 0-.5.5z"/></svg>;

function AnalysisModal({
    isOpen,
    onClose,
    isLoading,
    plotUrl,
    legendData,
    error,
    y2Limit,
    sensorsToPlot,
    viewHistory,
    historyIndex,
    interactionMode,
    allSensors,
    anomalousSensors,
    activeFile,
    sensorTranslations,
    onSensorChange,
    onY2LimitChange,
    onApplyY2Limit,
    onInteraction,
    onHome,
    onBack,
    onForward,
    onModeChange
}) {
    const handleSave = () => {
        if (!plotUrl) return;
        const link = document.createElement('a');
        link.href = plotUrl;
        link.download = `plot_${activeFile?.name || 'export'}_${new Date().getTime()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    
    // Create a map of SensorName -> { color, isY2 } for lookup
    const legendInfoMap = React.useMemo(() => {
        const map = {};
        if (legendData && Array.isArray(legendData)) {
            legendData.forEach(item => {
                const isY2 = item.name.includes('(Y2)');
                const baseId = item.name.replace('(Y2)', '').trim();
                map[baseId] = { color: item.color, isY2 };
            });
        }
        return map;
    }, [legendData]);

    if (!isOpen) return null;
    
    const sortedSensors = [...allSensors].sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        const numA = parseInt(nameA.replace('IW', ''), 10);
        const numB = parseInt(nameB.replace('IW', ''), 10);
        if (isNaN(numA) || isNaN(numB)) {
            return nameA.localeCompare(nameB);
        }
        return numA - numB;
    });

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div 
                className={`${styles.modalContent} ${styles.analysisModalContent}`} 
                onClick={(e) => e.stopPropagation()}
                data-tut="analysis-modal"
            >
                <div className={styles.modalHeader}>
                    <h2>Kombinierte Sensoranalyse für: {activeFile?.name}</h2>
                    <button 
                        onClick={onClose} 
                        className={styles.closeButton}
                        data-tut="analysis-close"
                    >×</button>
                </div>
                <div className={styles.analysisBody}>
                    <div className={styles.sensorChecklist} data-tut="analysis-sensor-list">
                        <h4>Sensoren für Diagramm auswählen</h4>
                        
                        {/* --- Moved Y2 Controls Here --- */}
                        <div className={styles.yAxisControls} data-tut="analysis-y2-limit" style={{ borderBottom: '1px solid var(--border-color-light)', marginBottom: '0.5rem', paddingBottom: '0.5rem' }}>
                            <label htmlFor="y2-limit">Y2-Achse Max-Limit:</label>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <input 
                                    type="number" 
                                    id="y2-limit" 
                                    value={y2Limit} 
                                    onChange={(e) => onY2LimitChange(Number(e.target.value))} 
                                    style={{ flexGrow: 1 }}
                                />
                                <button onClick={onApplyY2Limit} style={{ width: 'auto' }}>Anwenden</button>
                            </div>
                        </div>

                        <div className={styles.checklistItems}>
                            {sortedSensors.map(sensorObj => {
                                // Retrieve color and Y2 status for this sensor
                                const info = legendInfoMap[sensorObj.name];
                                const color = info ? info.color : null;
                                const isY2 = info ? info.isY2 : false;
                                
                                // Determine the display name (Translated Name + (Y2) if applicable)
                                const baseName = sensorTranslations[sensorObj.name] || sensorObj.name;
                                const displayName = isY2 ? `${baseName} (Y2)` : baseName;

                                return (
                                    <label key={sensorObj.name} className={`${styles.checklistLabel} ${anomalousSensors.includes(sensorObj.name) ? styles.anomalous : ''}`}>
                                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={sensorsToPlot.has(sensorObj.name)} 
                                                onChange={() => onSensorChange(sensorObj.name)} 
                                            />
                                            {/* Render color box if present in plot */}
                                            {color && (
                                                <span 
                                                    className={styles.legendColorBox} 
                                                    style={{ 
                                                        backgroundColor: color, 
                                                        marginRight: '8px',
                                                        marginLeft: '4px' 
                                                    }} 
                                                />
                                            )}
                                            
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={displayName}>
                                                {displayName}
                                            </span>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles.mainAnalysisArea}>
                        <div className={styles.plotAndToolbar}>
                            <div className={styles.plotToolbar} data-tut="analysis-toolbar">
                                <button onClick={onHome} title="Startansicht (Zurücksetzen)"><IconHome /></button>
                                <button onClick={onBack} disabled={historyIndex <= 0} title="Zurück"><IconBack /></button>
                                <button onClick={onForward} disabled={historyIndex >= viewHistory.length - 1} title="Vorwärts"><IconForward /></button>
                                <span className={styles.toolbarSeparator}></span>
                                
                                <span data-tut="analysis-mode-toggle" style={{ display: 'contents' }}>
                                    <button onClick={() => onModeChange('pan')} className={interactionMode === 'pan' ? styles.active : ''} title="Verschieben"><IconPan /></button>
                                    <button onClick={() => onModeChange('zoom')} className={interactionMode === 'zoom' ? styles.active : ''} title="Zoomen"><IconZoom /></button>
                                </span>
                                
                                <span className={styles.toolbarSeparator}></span>
                                <button onClick={handleSave} disabled={!plotUrl || isLoading} title="Diagramm speichern"><IconSave /></button>
                            </div>
                            <div className={styles.plotContainer} data-tut="analysis-plot">
                                {isLoading && <p>Lade Diagramm...</p>}
                                {error && <p className={styles.errorText}>{error}</p>}
                                {!isLoading && !error && plotUrl && (
                                    <InteractivePlotContainer 
                                        src={plotUrl}
                                        viewState={viewHistory[historyIndex]}
                                        onInteraction={onInteraction}
                                        interactionMode={interactionMode}
                                    />
                                )}
                                {!isLoading && !error && !plotUrl && (<p>Wählen Sie einen oder mehrere Sensoren aus, um ein Diagramm zu erstellen.</p>)}
                            </div>
                        </div>
                        {/* Formerly controlsAndLegend - Removed entirely as requested */}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AnalysisModal;